import { map, forIn, set, omit, findIndex, concat } from 'lodash'
import { http } from 'utils'

const initialFiltersState = () => ({
  opportunity_types: [],
  sport: [],
  region: [],
  club: [],
  based_in: [],
  available_in: [],
  app: 'all',
  manager: null,
  social_following: [],
  client: [],
  gender: [],
  theme: [],
  league: [],
  industry: [],
  packages: [],
  category_id: null,
  search_terms: '',
  first_name: '',
  last_name: '',
  search_area: 'name',
  age: null,
  visibility: null,
  location_region: [],
  category: null,
  position: [],
  award: [],
  highSchool: [],
  college: [],
  degreeCompleted: [],
  budgetRange: [],
  proBowl: [],
  charity: [],
  sponsor: [],
  firstContract: [],
  interestCategories: [],
  specialistTopics: [],
  personalInterests: [],
  retired: null,
  talentBudgetCategories: [],
  talentBudgetCategoryRanges: []
})

const initialPagination = () => ({
  current_page: 1,
  from: 1,
  last_page: 1,
  next_page_url: null,
  per_page: 10,
  prev_page_url: null,
  to: 1,
  total: 0
})

// Our stars state.
const state = {
  stars: [],
  selectedStars: [],
  filters: initialFiltersState(),
  filtersDisabled: false,
  flatFilters: [],
  pagination: initialPagination()
}

// Our stars state getters.
const getters = {
  sanitizedFilters: (state) => {
    function getId (val) {
      if (val.parent_id) {
        return val.parent_id
      }
      return val.id
    }

    let sanitized = {}

    forIn(state.filters, (value, key) => {
      sanitized[key] = map(value, getId)
    })
    sanitized['search_terms'] = state.filters.search_terms
    sanitized['first_name'] = state.filters.first_name
    sanitized['last_name'] = state.filters.last_name
    sanitized['featured_order'] = false
    sanitized['literal_match'] = true
    sanitized['category_id'] = state.filters.category_id
    sanitized['search_area'] = state.filters.search_area
    sanitized['app'] = state.filters.app
    sanitized['manager'] = state.filters.manager
    sanitized['age'] = state.filters.age
    sanitized['visibility'] = state.filters.visibility
    sanitized['booking_id'] = state.filters.booking_id
    sanitized['retired'] = state.filters.retired

    return sanitized
  },

  /**
   * Determine if we have no more stars to load.
   *
   * @return bool
   */
  haveNoMoreStars: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

// Our stars actions
const actions = {

  clearAllFilters ({ commit }) {
    commit('RESET_ALL_FILTERS')
  },

  clearFilter ({ commit }, filter) {
    commit('RESET_FILTER', filter)
  },

  getStars ({ commit }, params) {
    commit('RESET_PAGINATION')

    return http.get(`/our-stars/authed`, { params }).then(response => {
      commit('SET_STARS_FILTERING', response.data.data)
      commit('SET_STARS_PAGINATION_FILTERING', omit(response.data.meta, 'data'))
      return response
    })
  },

  /**
   * Set stars from a paginator object.
   *
   * Expects a "data" property that contains an array of stars, remaining properties
   * are expected to be paginator related from Laravel.
   *
   * @return Promise
   */
  setStars ({ commit }, paginator) {
    commit('SET_STARS_FILTERING', paginator.data)
    commit('SET_STARS_PAGINATION_FILTERING', omit(paginator, 'data'))
  },

  getFeaturedStars ({ commit }) {
    return http.get(`/featured-stars`)
  },

  getAllStarsFiltered ({ commit, state, getters }, params = {}) {
    params.pagination = false
    return http.get(`our-stars/authed`, { params })
      .then((response) => {
        commit('ADD_SELECTED_STARS', response.data)
        params.pagination = true
        return response
      })
  },

  getPageStars ({ commit, state, getters }, params = {}) {
    return http.get(`our-stars/authed`, { params })
      .then((response) => {
        commit('SET_STARS_FILTERING', response.data.data)
        commit('SET_STARS_PAGINATION_FILTERING', omit(response.data.meta, 'data'))

        return response
      })
  },

  removeFilter ({ commit, state }, filter) {
    commit('REMOVE_FILTER', filter)
    commit('REMOVE_FLAT_FILTER', filter)

    if (filter.key === 'star_name' && state.filters.star_name.length) {
      state.filtersDisabled = false
    }
  },

  setFilters ({ commit }, filters) {
    commit('SET_FILTERS', filters)
  },

  resetFilters ({ commit }) {
    commit('RESET_FILTERS')
  },

  setFiltersProperty ({ commit }, { property, value }) {
    commit('SET_FILTERS_PROPERTY', { property, value })
  },

  addFlatFilter ({ commit }, filter) {
    commit('ADD_FLAT_FILTER', filter)
  },

  setFiltersDisabled ({ commit }, disabled) {
    commit('SET_FILTERS_DISABLED', disabled)
  },

  addSelectedStars ({ commit }, stars) {
    commit('ADD_SELECTED_STARS', stars)
  },

  updateSelectedStarsBudget ({ commit }, { userId, budget }) {
    commit('UPDATE_SELECTED_STARS_BUDGET', { userId, budget })
  },

  setSelectedStars ({ commit }, stars) {
    commit('SET_SELECTED_STARS', stars)
  },

  removeSelectedStar ({ commit }, userId) {
    commit('REMOVE_SELECTED_STAR', userId)
  },

  resetSelectedStars ({commit}) {
    commit('SET_SELECTED_STARS', [])
  }
}

// Our stars state mutations.
const mutations = {
  SET_STARS_FILTERING (state, stars) {
    state.stars = stars
  },

  ADD_STARS_FILTERING (state, stars) {
    state.stars = concat(state.stars, stars)
  },

  SET_FILTERS_DISABLED (state, value) {
    state.filtersDisabled = value
  },

  SET_FILTERS (state, value) {
    state.filters = value
  },

  RESET_FILTERS (state) {
    state.filters = initialFiltersState()
  },

  SET_STARS_PAGINATION_FILTERING (state, pagination) {
    state.pagination = pagination
  },

  SET_FILTERS_PROPERTY (state, { property, value }) {
    set(state.filters, property, value)
  },

  ADD_FLAT_FILTER (state, item) {
    let existingFilter = findIndex(state.flatFilters, existing => (String(existing.id) === String(item.id) && existing.key === item.key))

    if (existingFilter <= -1) {
      state.flatFilters.push(item)
    }
  },

  REMOVE_FILTER (state, item) {
    let existingFilter = findIndex(state.filters[item.key], existing => String(existing.id) === String(item.id))

    if (existingFilter > -1) {
      state.filters[item.key].splice(existingFilter, 1)
    }
  },

  REMOVE_FLAT_FILTER (state, item) {
    let existingFilter = findIndex(state.flatFilters, existing => (String(existing.id) === String(item.id) && existing.key === item.key))

    if (existingFilter > -1) {
      state.flatFilters.splice(existingFilter, 1)
    }
  },

  RESET_PAGINATION (state) {
    state.pagination = initialPagination()
  },

  RESET_ALL_FILTERS (state) {
    state.filters = initialFiltersState()
    state.flatFilters = []
  },

  RESET_FILTER (state, filter) {
    state.filters[filter] = initialFiltersState()[filter]
  },

  ADD_SELECTED_STARS (state, stars) {
    const selectedStarIds = state.selectedStars.map(item => item.id)
    if (!Array.isArray(stars)) {
      stars = [stars]
    }
    const filteredStars = stars.filter(element => {
      return !selectedStarIds.includes(element.id)
    })
    state.selectedStars = concat(state.selectedStars, filteredStars)
  },
  SET_SELECTED_STARS (state, stars) {
    state.selectedStars = stars
  },
  REMOVE_SELECTED_STAR (state, id) {
    let index = findIndex(state.selectedStars, user => user.id === id)
    if (index !== -1) {
      state.selectedStars.splice(index, 1)
    }
  },
  UPDATE_SELECTED_STARS_BUDGET (state, { userId, budget }) {
    let index = findIndex(state.selectedStars, user => user.id === userId)
    if (index !== -1) {
      state.selectedStars[index]['budget'] = budget
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
